import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogModule, DialogService, WindowCloseResult, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { GridModule, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { PagerModule } from '@progress/kendo-angular-pager';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { faAddressCard, faEdit, faPlusCircle, faTrash, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { DeleteConfirmComponent } from '../../common-components/delete-confirm/delete-confirm.component';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import {
  CasiProjectUser, Project, User as UserModel
} from '../../models/models.lib';
import { AppEnities, CasiCloseOptions, defaultGridPageSize, FormModes } from '../../common/constants.lib';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ProjectUserService } from '../../tracker/project-user.service';
import { ProjectUserSelectionComponent } from '../project-user-selection/project-user-selection.component';

const { PROJECT_USER } = AppEnities;

@Component({
  selector: 'casi-project-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    WindowModule,
    PagerModule,
    GridModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    LayoutModule,
    MulticheckFilterComponent,
    FontAwesomeModule,
    DeleteConfirmComponent,
    ErrorDialogModalComponent,
    ProjectUserSelectionComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './project-users.component.html',
  styleUrl: './project-users.component.scss'
})
export class ProjectUsersComponent implements OnInit, OnDestroy {

  @Input({ required: true }) project: Project | undefined;
  @Input({ required: true }) users: CasiProjectUser[] | undefined;

  @Output() onClose = new EventEmitter<void>();

  public rawData: CasiProjectUser[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: CasiProjectUser[] = filterBy(this.rawData, this.filter);

  public currentItem: CasiProjectUser | undefined; // = new CasiProjectUser();

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";
  public pageSize = defaultGridPageSize;

  public canUpdate: boolean = true;
  public canCreate: boolean = true;
  public canDestroy: boolean = true;
  public formMode: FormModes = FormModes.NOTSET;
  public editWinOpen: boolean = false;
  public usersWinOpen: boolean = false;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  private _windowRef: WindowRef | undefined;

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    public dataService: ProjectUserService,
    private toastr: ToastrService,
    public faIcons: FaIconLibrary,
    private me: MeService,
    private dialogService: DialogService,
    private windowService: WindowService

  ) {
    faIcons.addIcons(faPlusCircle, faAddressCard, faEdit,
      faTrash, faTriangleExclamation);
    faIcons.addIcons(...all);
  }

  public sort: SortDescriptor[] = [
    {
      field: "user_id",
      dir: "asc",
    },
  ];

  //#region Ng Handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...', this.users);
    // const { can_Create, can_Update, can_Destroy } = this.me.allICanDo(PROJECT_USER)
    // this.canUpdate = can_Update;
    // this.canCreate = can_Create;
    // this.canDestroy = can_Destroy;
    // this.logger.debug('ngOnInit:permissions: ', this.canCreate, can_Create);

    this.dataService.query(this.project!.id);
    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:e: ', e);
      this.rawData = e;
      this.loadData();
    }))
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Public functions

  closeWindow(): void {
    this.logger.debug(`closeWindow...`);
    this.onClose.emit();
  }

  onAddUser(titleBar: TemplateRef<unknown>): void {
    this.logger.debug(`onAddUser...`);
    this.closeWindowRef();
    this.formMode = FormModes.NEW;
    this.showTitle = 'VIEW USERS';
    this._windowRef = this.windowService.open({
      // title: setTitle(this.formMode, 'USER DETAILS'),
      titleBarContent: titleBar,
      content: ProjectUserSelectionComponent,
      width: 900,
      minWidth: 320
    });
    this.setViewUsersInstanceInputs();

    this._windowRef.result.subscribe(results => {
      this.logger.debug('onViewUsers:results: ', results);
      if (results instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.onCancel.subscribe(() => {
      this.logger.debug('onViewUsers:onClose');
      this.closeWindowRef();
    });

    this._windowRef.content.instance.onSave.subscribe((projectUsers: CasiProjectUser[]) => {
      this.logger.debug('onViewUsers:onSave', projectUsers);
      this.updateProjectUsers(projectUsers);
    });

    this.usersWinOpen = true;
  }

  //#endregion

  //#region Grid handlers

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    // this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.rawData, fieldName).map((item: any) => item[fieldName]);
    // return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region view

  setActive(dataItem: any) {
    this.logger.debug('setActive:dataItem: ', dataItem);
    const { id } = dataItem;
    // dataItem.active = !dataItem.active;
    // this.dataService.update(dataItem)
    //   .then(() => {
    //     this.notify(dataItem.active ? 'Project Activated' : 'Project Deactivated')
    //     this.dataService.query();
    //   })
    //   .catch(err => {
    //     this.logger.error('setActive:error: ', err);
    //     this.notify("Error Activated/Deactivated Project", true);
    //   })
  };

  public onAddClicked(): void {
    this.logger.debug("onAddClicked...");
    // this.currentItem = new ProjectNew();
    // this.formMode = FormModes.NEW;
    // this.logger.debug("onAddClicked:currentItem ", this.currentItem);
    // this.showTitle = setTitle(this.formMode, 'PROJECT');
    // this.editWinOpen = true;
  }

  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    // this.currentItem = Project.create(dataItem);
    // this.formMode = FormModes.EDIT;
    // this.logger.debug("onEditClick:currentItem ", this.currentItem);
    // this.showTitle = setTitle(this.formMode, 'PROJECT');
    // this.editWinOpen = true;
  }

  public onItemSave(args: any): void {
    this.logger.debug('onItemSave...', args);
    this.handleSaveAndClose(args);
  }

  onConfirmDelete(dataItem: any): void {
    this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentItem = dataItem;
    this.openConfirmDailog(this.currentItem);
  }

  public close(): void {
    this.logger.debug('close...');
    this.editWinOpen = false;
  }

  viewUsers(dataItem: any): void {
    this.logger.debug("viewUsers: ", dataItem);
    // this.currentItem = new Project(dataItem);
    // this.usersWinOpen = true;
  }

  public closeUsers(): void {
    this.logger.debug('closeUsers...');
    this.dataService.query(this.project!.id);
    this.usersWinOpen = false;
  }

  public openConfirmDailog(dataItem: any): void {
    this.logger.debug("openConfirmDailog: ", dataItem);
    this.currentItem = dataItem;
    const dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 320,
      width: 750,
      // cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete Project User - <b>${this.currentItem?.email}</b>?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentItem);
        this.handleRemove(this.currentItem!);
      }
    });
  }

  //#endregion

  //#region Private functions

  private loadData(): void {
    this.logger.debug('loadData...');
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  private handleSaveAndClose(args: any): void {
    this.logger.debug('handleSaveAndClose...', args);
    const { action, data, mode } = args;
    // this.dataService.addEdit(data, mode)
    //   .then(resp => {
    //     this.notify(mode === FormModes.NEW ? 'Project Added' : 'Project Updated')
    //     this.dataService.query();
    //   })
    //   .catch(err => {
    //     this.logger.error('handleSaveAndClose:error: ', err);
    //     this.notify("Error Adding/Updating Project", true);
    //   })
    //   .finally(() => this.editWinOpen = false);
  }

  private handleRemove(args: CasiProjectUser): void {
    this.logger.debug(`handleRemove: args: `, args);
    if (args && !args.id) {
      this.logger.error('handlRemove: No id given');
    } else {
      this.dataService.destroy(args.id!)
        .then(resp => {
          this.logger.debug('handleRemove:resp: ', resp);
          this.dataService.query(this.project!.id);
          this.notify('Project User Deleted');
        })
        .catch(err => {
          this.logger.error('handleRemove:err: ', err);
          this.openErrorDailog();
        });
    }
  }

  private openErrorDailog(): void {
    // const dialogRef = this.dialogService.open({
    //   content: ErrorDialogModalComponent,
    //   minWidth: 320,
    //   width: 750,
    //   cssClass: "custom-css-class"
    // });

    // const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    // contentRef.title = "Update Issue";
    // contentRef.content = `The current project: ${this.currentItem?.project_name} had a problem being removed.`;
    // contentRef.buttonText = "Close";
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.usersWinOpen = false;
  }

  private setViewUsersInstanceInputs(): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.project = this.project;
      inputs.users = this.users?.map(u => new CasiProjectUser(u));
      inputs.projectUsers = this.gridData;
      this.logger.debug('setViewUsersInstanceInputs: ', inputs);
    }
  }

  private updateProjectUsers(users: CasiProjectUser[]): void {
    this.logger.debug(`updateProjectUsers: `, users, this.currentItem);
    this.closeWindowRef();
    let toUpdated = {
      project_id: this.project!.id,
      users
    };
    this.logger.debug(`updateProjectUser:toUpdate: `, toUpdated);
    this.dataService.bulkUpdate(toUpdated)
      .then(resp => {
        this.logger.debug(`updateProjectUsers:resp: `, resp);
        this.dataService.query(this.project!.id);
      })
      .catch(err => {
        this.logger.error(`updateProjectUsers:error: `, err);
      })
  }

  //#endregion

}

